import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import {withStyles} from '@material-ui/core/styles'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import styled from 'styled-components'
import {onChangeCheckedHandler, onUnknownChangeHandler} from 'lib/dom'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {spacing} from 'lib/ui/theme'
import RoomList from 'organization/Event/Area/RoomList'
import {useArea} from 'organization/Event/Area/AreaProvider'
import {useAreaRoutes} from 'organization/Event/Area/AreaRoutes'
import React, {useState} from 'react'
import Page from 'organization/Event/Page'
import HasPermission from 'organization/HasPermission'
import {
  EVENT_SETTINGS,
  START_AREAS,
  VIEW_RECORDINGS,
} from 'organization/PermissionsProvider'
import ExportAreaAttendees from 'organization/Event/Area/ExportAreaAttendees'
import ClearRoomAssignmentsButton from 'organization/Event/Area/ClearRoomAssignmentsButton'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import {useToggle} from 'lib/toggle'
import ConfirmDialog from 'lib/ui/ConfirmDialog'
import {useRooms} from 'organization/Event/Area/RoomsProvider'
import {
  Area as AreaModel,
  BALANCED,
  LOAD_BALANCE_STRATEGIES,
} from 'organization/Event/AreasProvider'
import NameField from 'organization/Event/Area/NameField'
import DeleteAreaButton from 'organization/Event/Area/DeleteAreaButton'
import OfflineTextConfig from 'organization/Event/Area/OfflineTextConfig'
import RegistrationSwitch from 'organization/Event/Area/RegistrationSwitch'
import {ENTERPRISE} from 'obvio/Billing/plans'
import IfOwnerHasPlan from 'organization/auth/IfOwnerPlan'
import Tooltip from '@material-ui/core/Tooltip'
import VideocamIcon from '@material-ui/icons/Videocam'
import {
  PageBreadcrumbProps,
  ZoomAreasBreadcrumbs,
} from 'organization/Event/Page/PageBreadcrumbs'
import Select from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import AreaRegistrationURL from 'organization/Event/Area/AreaRegistrationURL'
import {RecordingIconImage} from 'organization/Event/Room'
import recordingIcon from 'assets/images/recording-icon.png'
import {roomRoutes} from 'organization/Event/Room/RoomRoutes'
import {useEvent} from 'Event/EventProvider'
import {Breadcrumb, WithBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import {useEventRoutes} from 'organization/Event/EventRoutes'

const SETTING_CANNOT_BE_CHANGED_DESCRIPTION =
  'This setting can only be modified at the time that you create the area.'

const CONFIRM_TURN_OFF_TECH_CHECK_REASSIGN =
  "This area is currently assigned to Tech Check.  If you turn this setting off, and an attendee hasn't completed their tech check, they may see an offline message when they try to check in and their original room is unavailable.  Are you sure you wish to disable?"

export default function Area() {
  const {area, update, processing} = useArea()
  const routes = useAreaRoutes()
  const {rooms, totalAttendees} = useRooms()
  const areaRoutes = useAreaRoutes()
  const [error, setError] = useState<string | null>(null)
  const clearError = () => setError(null)
  const {
    flag: showingConfirmReassignDialog,
    toggle: toggleConfirmReassignDialog,
  } = useToggle()

  const hasOneRoom = rooms.length === 1
  const singleRoomRoute = hasOneRoom
    ? roomRoutes({areaRoutes, room: rooms[0]})
    : null

  const save = (data: Partial<AreaModel>) => {
    clearError()
    return update(data).catch((e) => setError(e.message))
  }

  const updateKey = <T extends keyof AreaModel>(key: T) => (
    val: AreaModel[T],
  ) =>
    save({
      [key]: val,
    })

  /**
   * If an area is assigned to Tech Check, we want to confirm with the
   * user that they'd really want to turn OFF re-assign.
   * @param reassign
   * @returns
   */
  const handleSetReassign = (reassign: boolean) => {
    // Not TC, free to turn on/off
    if (!area.is_tech_check) {
      save({
        reassign_on_offline: reassign,
      })
      return
    }

    // Don't need to confirm on turning on
    if (reassign) {
      save({
        reassign_on_offline: reassign,
      })
      return
    }

    toggleConfirmReassignDialog()
  }

  const turnOffTechCheckReassign = () => {
    /**
     * Only allow turning off TC re-assign via confirm dialog
     */
    if (!showingConfirmReassignDialog) {
      return
    }

    save({reassign_on_offline: false})
    toggleConfirmReassignDialog()
  }

  const [isZoomSettingsVisible, setISZoomSettingsVisible] = useState(false)

  const handleZoomSettingsToggle = () => {
    setISZoomSettingsVisible(!isZoomSettingsVisible)
  }

  return (
    <>
      <ConfirmDialog
        description={CONFIRM_TURN_OFF_TECH_CHECK_REASSIGN}
        onConfirm={turnOffTechCheckReassign}
        showing={showingConfirmReassignDialog}
        onCancel={toggleConfirmReassignDialog}
      />
      <ZoomAreasBreadcrumbs page={area.name}>
        <Page>
          <ErrorAlert>{error}</ErrorAlert>
          <HasPermission required={EVENT_SETTINGS}>
            <Box>
              <Left>
                <ExportAreaAttendees area={area} />
                <RelativeLink to={areaRoutes.rules} disableStyles>
                  <RulesButton variant="outlined" aria-label="Area Rules">
                    Rules
                  </RulesButton>
                </RelativeLink>
                <div>
                  <ZoomSettingsButton
                    variant="outlined"
                    aria-label="Zoom Settings"
                    onClick={handleZoomSettingsToggle}
                  >
                    <VideocamIcon style={{marginRight: '8px'}} />
                    {isZoomSettingsVisible
                      ? 'Hide Zoom Settings'
                      : 'Zoom Settings'}
                  </ZoomSettingsButton>
                </div>
              </Left>
              <RightButtons>
                <div>
                  <ClearRoomAssignmentsButton
                    onError={setError}
                    clearError={clearError}
                  />
                </div>
                {hasOneRoom && singleRoomRoute && (
                  <HasPermission required={VIEW_RECORDINGS}>
                    <RelativeLink to={singleRoomRoute.recordings} disableStyles>
                      <Button variant="outlined">
                        <RecordingIconImage
                          src={recordingIcon}
                          alt="Record icon"
                        />
                        Recordings
                      </Button>
                    </RelativeLink>
                  </HasPermission>
                )}
              </RightButtons>
            </Box>
          </HasPermission>
          <NameField />
          <DeleteAreaButton />
          <HasPermission required={[EVENT_SETTINGS, START_AREAS]}>
            <Box>
              <FormControlLabel
                disabled={processing}
                control={
                  <Switch
                    checked={area.is_open}
                    onChange={onChangeCheckedHandler(updateKey('is_open'))}
                    color="primary"
                    inputProps={{
                      'aria-label': 'toggle area open status',
                    }}
                  />
                }
                label="Open Area"
              />
              <HasPermission required={EVENT_SETTINGS}>
                <FormControlLabel
                  disabled={processing}
                  control={
                    <Switch
                      checked={area.reassign_on_offline}
                      onChange={onChangeCheckedHandler(handleSetReassign)}
                      color="primary"
                      inputProps={{
                        'aria-label': 'toggle re-assign on offline',
                      }}
                    />
                  }
                  label="Re-assign on offline"
                />
              </HasPermission>
            </Box>
          </HasPermission>
          <HasPermission required={EVENT_SETTINGS}>
            <ZoomSettingsContent
              className={isZoomSettingsVisible ? 'show' : 'hide'}
              id="zoom-settings"
              aria-label="zoom settings"
            >
              <Title variant="h5" align="left" style={{marginTop: spacing[6]}}>
                Zoom Settings
                <HideText
                  aria-label="hide zoom settings"
                  onClick={handleZoomSettingsToggle}
                >
                  hide
                </HideText>
              </Title>
              <Title variant="h6" align="left">
                Room Assignment Settings
              </Title>
              <FieldsHolder>
                <StyledFieldset required fullWidth disabled={processing}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={area.clears_room_assignments_on_update}
                        onChange={onChangeCheckedHandler(
                          updateKey('clears_room_assignments_on_update'),
                        )}
                        inputProps={{
                          'aria-label':
                            'toggle clear room assignment on attendee update',
                        }}
                      />
                    }
                    label="Clear room assignment on attendee update"
                  />
                  <Info>
                    When enabled, updating an attendee&apos;s tags or groups
                    will also clear their room assignment in this area, in
                    addition to their join link.
                  </Info>
                </StyledFieldset>
              </FieldsHolder>
              <Title variant="h6" align="left">
                Security Settings
              </Title>
              <FieldsHolder>
                <StyledFieldset required fullWidth disabled>
                  <Tooltip
                    title={SETTING_CANNOT_BE_CHANGED_DESCRIPTION}
                    placement="bottom-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={area.requires_approval}
                          onChange={onChangeCheckedHandler(
                            updateKey('requires_approval'),
                          )}
                          inputProps={{
                            'aria-label': 'toggle requires approval',
                          }}
                        />
                      }
                      label="Requires approval to join area meetings"
                    />
                  </Tooltip>
                  <Info>
                    <strong>This is a security feature</strong> to prevent
                    non-registered attendees from joining your meetings.
                    <br />
                    <br />
                    Enabling this won't allow users to just copy the Zoom links
                    and share them with others.
                  </Info>
                </StyledFieldset>
                <StyledFieldset required fullWidth disabled>
                  <Tooltip
                    title={SETTING_CANNOT_BE_CHANGED_DESCRIPTION}
                    placement="bottom-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={area.allows_multiple_devices}
                          inputProps={{
                            'aria-label': 'toggle allows multiple devices',
                          }}
                          onChange={onChangeCheckedHandler(
                            updateKey('allows_multiple_devices'),
                          )}
                        />
                      }
                      label="Same user can join from multiple devices"
                    />
                  </Tooltip>
                  <Info>
                    Enabling this will allow users to join Zoom from multiple
                    devices (e.g., laptop and phone) simultaneously.{' '}
                    <strong>This is a security risk</strong> as an attendee
                    could share their personal link with others, allowing
                    multiple users to join this area.
                  </Info>
                </StyledFieldset>
              </FieldsHolder>
              <Title variant="h6" align="left">
                Question and Answer Settings
              </Title>
              <FieldsHolder>
                <StyledFieldset required fullWidth disabled>
                  <Tooltip
                    title={SETTING_CANNOT_BE_CHANGED_DESCRIPTION}
                    placement="bottom-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={area.zoom_enable_q_and_a}
                          inputProps={{
                            'aria-label': 'toggle enable q and a',
                          }}
                          onChange={onChangeCheckedHandler(
                            updateKey('zoom_enable_q_and_a'),
                          )}
                        />
                      }
                      label="Enable Zoom Q&A"
                    />
                  </Tooltip>
                  <Info>
                    Allow attendees to ask questions for the host and panelists
                    to answer
                  </Info>
                </StyledFieldset>
              </FieldsHolder>
              <Title variant="h6" align="left">
                Interpretation Settings
              </Title>
              <FieldsHolder>
                <StyledFieldset required fullWidth disabled>
                  <Tooltip
                    title={SETTING_CANNOT_BE_CHANGED_DESCRIPTION}
                    placement="bottom-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={area.zoom_enable_language_interpretation}
                          inputProps={{
                            'aria-label':
                              'toggle enable language interpretation',
                          }}
                          onChange={onChangeCheckedHandler(
                            updateKey('zoom_enable_language_interpretation'),
                          )}
                        />
                      }
                      label="Enable language interpretation"
                    />
                  </Tooltip>
                  <Info>
                    Allow host to assign participants as interpreters who can
                    interpret one language into another in real-time. Host can
                    assign interpreters when scheduling or during the meeting.
                  </Info>
                </StyledFieldset>
                <StyledFieldset required fullWidth disabled>
                  <Tooltip
                    title={SETTING_CANNOT_BE_CHANGED_DESCRIPTION}
                    placement="bottom-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            area.zoom_enable_sign_language_interpretation
                          }
                          inputProps={{
                            'aria-label':
                              'toggle enable sign language interpretation',
                          }}
                          onChange={onChangeCheckedHandler(
                            updateKey(
                              'zoom_enable_sign_language_interpretation',
                            ),
                          )}
                        />
                      }
                      label="Enable sign language interpretation"
                    />
                  </Tooltip>
                  <Info>
                    Allow host to assign participants as sign language
                    interpreters who can interpret one language into sign
                    language in real-time. Host can assign interpreters when
                    scheduling or during the meeting.
                  </Info>
                </StyledFieldset>
              </FieldsHolder>
              <Title variant="h6" align="left">
                Notification Settings
              </Title>
              <FieldsHolder>
                <StyledFieldset required fullWidth disabled={processing}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={area.sends_recording_ready_notifications}
                        onChange={onChangeCheckedHandler(
                          updateKey('sends_recording_ready_notifications'),
                        )}
                        inputProps={{
                          'aria-label':
                            'toggle notify team when recordings are ready',
                        }}
                      />
                    }
                    label="Notify team when recordings are ready"
                  />
                  <Info>
                    Send an email to the organization owner and to team members
                    with the "Recordings Ready Notification" permission whenever
                    a Zoom recording for this area finishes processing.
                  </Info>
                </StyledFieldset>
              </FieldsHolder>
            </ZoomSettingsContent>
            <IfOwnerHasPlan plan={ENTERPRISE}>
              <FormControl>
                <FormControlLabel
                  control={<RegistrationSwitch />}
                  label="Enable Direct Area Access"
                />
              </FormControl>
              <AreaRegistrationURL />
            </IfOwnerHasPlan>
            <IfOwnerHasPlan plan={ENTERPRISE}>
              <StyledSelect
                value={area.load_balance_strategy ?? BALANCED}
                disabled={processing}
                fullWidth
                onChange={onUnknownChangeHandler(
                  updateKey('load_balance_strategy'),
                )}
                label="Attendee Room Distribution"
                aria-label="pick load balancing strategy"
              >
                {LOAD_BALANCE_STRATEGIES.map((strategy) => (
                  <StyledOption
                    key={strategy}
                    value={strategy}
                    aria-label={`pick ${strategy}`}
                  >
                    {strategy}
                  </StyledOption>
                ))}
              </StyledSelect>
            </IfOwnerHasPlan>
            <OfflineTextSection />
            <RelativeLink to={routes.rooms.create} disableStyles>
              <CreateRoomButton
                variant="outlined"
                color="primary"
                aria-label="create rooms"
              >
                Create Rooms
              </CreateRoomButton>
            </RelativeLink>
          </HasPermission>
          <Typography>Total Attendees: {totalAttendees}</Typography>
          <RoomList rooms={rooms} isTechCheck={area.is_tech_check} />
        </Page>
      </ZoomAreasBreadcrumbs>
    </>
  )
}

function OfflineTextSection() {
  const {flag: configVisible, toggle: toggleConfig} = useToggle()

  return (
    <>
      <OfflineTextConfig isVisible={configVisible} onClose={toggleConfig} />
      <OfflineTextButton
        onClick={toggleConfig}
        variant="outlined"
        color="primary"
        aria-label="offline text button"
      >
        Offline Text
      </OfflineTextButton>
    </>
  )
}

export function AreaBreadcrumbs(props: PageBreadcrumbProps) {
  const areaRoutes = useAreaRoutes()
  const {area} = useArea()
  const eventRoutes = useEventRoutes()
  const {event} = useEvent()

  const parent = props.parent || {
    title: event.name,
    url: eventRoutes.root,
  }

  const breadcrumbs: Breadcrumb[] = [
    parent,
    {
      title: area.name,
      url: areaRoutes.root,
    },
    {
      title: props.page,
    },
  ]

  return (
    <WithBreadcrumbs breadcrumbs={breadcrumbs}>
      {props.children}
    </WithBreadcrumbs>
  )
}

const OfflineTextButton = withStyles({
  root: {
    marginBottom: spacing[4],
    display: 'block',
  },
})(Button)

const CreateRoomButton = withStyles({
  root: {
    marginBottom: spacing[4],
  },
})(Button)

const Box = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const Left = styled.div`
  display: flex;
`

const RulesButton = styled(Button)`
  margin-left: ${(props) => props.theme.spacing[2]};
`

const ZoomSettingsButton = styled(Button)`
  margin-left: ${(props) => props.theme.spacing[2]};
`

const HideText = styled.span`
  margin-left: 8px;
  font-size: 0.9rem;
  color: #666;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #333;
  }
`

const StyledOption = styled(Option)`
  text-transform: capitalize;
`

const StyledSelect = styled(Select)`
  text-transform: capitalize;
`

const RightButtons = styled.div`
  display: flex;
  gap: 15px;
`

const Title = withStyles({
  root: {
    marginBottom: spacing[4],
  },
})(Typography)

const Info = styled.div`
  font-size: 0.9rem;
  opacity: 0.38;
  margin-top: 4px;
  margin-left: 32px;
  font-style: italic;
`

const StyledFieldset = styled(FormControl)`
  width: 100%;
  border-radius: 8px;
  padding: 0 20px;
  cursor: pointer;
  padding-bottom: 10px;

  justify-content: top;

  &:hover {
    background: #f5f5f5;
  }
`

const FieldsHolder = styled.div`
  margin-bottom: ${(props) => `${props.theme.spacing[4]}`};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }

  & > div {
    width: 100%;
  }
`

const ZoomSettingsContent = styled.div`
  &.show {
    display: block;
  }

  &.hide {
    display: none;
  }
`
